// src/main.tsx — client entry point.
//
// Responsibilities:
//   1. TEL-02 telemetry wiring: window.unhandledrejection + React error
//      boundary both POST to /api/log via navigator.sendBeacon with a
//      per-page-load sessionId (stateless — no cross-session tracking).
//   2. Mount <App/> inside <StrictMode> + <ErrorBoundary>.
//
// The sessionId is a per-page-load correlator (not an auth signal); Plan 05's
// /api/log endpoint bypasses the auth gate specifically because sendBeacon
// fires on page unload and cannot follow a redirect / retry a 401.

import { Component, StrictMode, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './ui/App';

// TEL-02 — session id per page load (stateless, per CONTEXT.md; no cross-session tracking).
const SESSION_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export interface TelemetryPayload {
  sessionId: string;
  error: {
    type: 'unhandledRejection' | 'renderError';
    message: string;
    stack?: string;
  };
  timestamp: number;
}

export function sendTelemetry(payload: TelemetryPayload): void {
  // navigator.sendBeacon is fire-and-forget; returns false if queuing failed
  // (e.g., size limit). We intentionally do not retry — telemetry is
  // best-effort and must never break the game.
  try {
    const body = JSON.stringify(payload);
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon('/api/log', blob);
  } catch {
    // Swallow — telemetry failure must never break the game.
  }
}

// TEL-02 — unhandled promise rejections.
window.addEventListener('unhandledrejection', (event) => {
  const reason =
    event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
  sendTelemetry({
    sessionId: SESSION_ID,
    error: {
      type: 'unhandledRejection',
      message: String(reason),
      ...(reason.stack ? { stack: reason.stack } : {}),
    },
    timestamp: Date.now(),
  });
});

// TEL-02 — render errors (React error boundary).
// Class component because React does not yet have a hook-based boundary.
// getDerivedStateFromError flips `hasError` once so repeat errors don't
// flood /api/log (T-00-04-03 mitigation).
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo): void {
    sendTelemetry({
      sessionId: SESSION_ID,
      error: {
        type: 'renderError',
        message: error.message,
        ...(error.stack
          ? { stack: error.stack }
          : info.componentStack
            ? { stack: info.componentStack }
            : {}),
      },
      timestamp: Date.now(),
    });
  }
  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            color: '#f0ead8',
            padding: '1rem',
            fontFamily: 'ui-sans-serif',
          }}
        >
          Something went wrong. Please refresh.
        </div>
      );
    }
    return this.props.children;
  }
}

export { SESSION_ID };

/**
 * UAT Test 2 follow-up: dev-only console→disk bridge. When running `pnpm dev`
 * (`import.meta.env.DEV` is true), override console.log to also POST the
 * formatted message to the Vite middleware at `/__uat_log`, which appends to
 * `.planning/debug/uat-live.log`. Lets the debugging assistant Read the log
 * directly instead of asking the human to copy-paste from DevTools. Excluded
 * from production builds (the `if (import.meta.env.DEV)` guard is
 * constant-folded by Vite so the override never ships).
 */
if (import.meta.env.DEV) {
  const origLog = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    origLog(...args);
    try {
      const line = args
        .map((a) => {
          if (typeof a === 'string') return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(' ');
      const ts = new Date().toISOString();
      const blob = new Blob([`${ts} ${line}\n`], { type: 'text/plain' });
      // sendBeacon is fire-and-forget; if the dev server is gone, nothing breaks.
      navigator.sendBeacon?.('/__uat_log', blob);
    } catch {
      /* ignore — log bridge is best-effort */
    }
  };
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
